import { hmac } from "@noble/hashes/hmac";
import { sha256 } from "@noble/hashes/sha256";
import { bytesToHex, hexToBytes, utf8ToBytes } from "@noble/hashes/utils";
import { edwardsToMontgomeryPriv, edwardsToMontgomeryPub, x25519 } from "@noble/curves/ed25519";
import { fromB64Url, generateGroupKey, isHex64, toB64Url } from "./crypto.js";
import type { Identity } from "./crypto.js";
import type { KeyRotateBody, WrappedKey } from "./op.js";

// Changing the group key without anybody having to publish anything first.
//
// A member's id already IS an Ed25519 public key, and Ed25519 keys map onto
// X25519 ones, so the rotating member can wrap the new key straight to every
// remaining member using material the ledger already holds. That is the whole
// reason this can be added to a live group: no key-announcement round, no
// waiting for everyone to come online and republish.
//
// What rotation does NOT do, and cannot: take back what somebody already read.
// Whoever held the old key keeps every entry written under it. Rotation closes
// the door going forward, and the app says exactly that rather than implying
// something stronger.

const NONCE_BYTES = 12;

function wrapKeyFor(shared: Uint8Array, epoch: number, member: string): Uint8Array {
  return hmac(sha256, shared, utf8ToBytes(`psplit/rekey/v1|${epoch}|${member}`));
}

async function aesKey(raw: Uint8Array, usage: KeyUsage): Promise<CryptoKey> {
  return crypto.subtle.importKey("raw", raw as BufferSource, "AES-GCM", false, [usage]);
}

async function seal(wrapping: Uint8Array, groupKey: Uint8Array): Promise<string> {
  const nonce = crypto.getRandomValues(new Uint8Array(NONCE_BYTES));
  const ct = new Uint8Array(
    await crypto.subtle.encrypt(
      { name: "AES-GCM", iv: nonce as BufferSource },
      await aesKey(wrapping, "encrypt"),
      groupKey as BufferSource,
    ),
  );
  const out = new Uint8Array(nonce.length + ct.length);
  out.set(nonce, 0);
  out.set(ct, nonce.length);
  return toB64Url(out);
}

async function unseal(wrapping: Uint8Array, wrapped: string): Promise<Uint8Array | null> {
  try {
    const bytes = fromB64Url(wrapped);
    if (bytes.length <= NONCE_BYTES) return null;
    const plain = new Uint8Array(
      await crypto.subtle.decrypt(
        { name: "AES-GCM", iv: bytes.subarray(0, NONCE_BYTES) as BufferSource },
        await aesKey(wrapping, "decrypt"),
        bytes.subarray(NONCE_BYTES) as BufferSource,
      ),
    );
    return plain.length === 32 ? plain : null;
  } catch {
    return null;
  }
}

export interface RotationPlan {
  groupKey: Uint8Array; // the new key, to be stored locally under `epoch`
  body: KeyRotateBody;
}

/**
 * Build a rotation for exactly `members`. Whoever is missing from that list is
 * evicted by arithmetic rather than by policy: there is no wrapped key for them,
 * so the next epoch is simply unreadable.
 */
export async function planRotation(
  epoch: number,
  members: Iterable<string>,
  groupKey: Uint8Array = generateGroupKey(),
): Promise<RotationPlan> {
  const ephPriv = x25519.utils.randomPrivateKey();
  const ephPub = x25519.getPublicKey(ephPriv);
  // Sorted, because validation requires it and because a stable order keeps the
  // op byte-identical wherever it is rebuilt.
  const sorted = [...new Set(members)].sort();
  const keys: WrappedKey[] = [];
  for (const member of sorted) {
    if (!isHex64(member)) throw new Error("member is not a pubkey");
    const shared = x25519.getSharedSecret(ephPriv, edwardsToMontgomeryPub(hexToBytes(member)));
    keys.push({ m: member, k: await seal(wrapKeyFor(shared, epoch, member), groupKey) });
  }
  return { groupKey, body: { epoch, eph: bytesToHex(ephPub), keys } };
}

/**
 * Recover the new key from a rotation, or null if it was not addressed to this
 * identity. Null is the ordinary answer for anyone who was removed, and for a
 * member reading a rotation from a group they have since left.
 */
export async function unwrapRotation(
  body: KeyRotateBody,
  identity: Identity,
): Promise<Uint8Array | null> {
  const mine = body.keys.find((wrapped) => wrapped.m === identity.pubKey);
  if (!mine) return null;
  let shared: Uint8Array;
  try {
    shared = x25519.getSharedSecret(
      edwardsToMontgomeryPriv(hexToBytes(identity.privKey)),
      hexToBytes(body.eph),
    );
  } catch {
    return null;
  }
  return unseal(wrapKeyFor(shared, body.epoch, identity.pubKey), mine.k);
}
